import {
  Availability,
  FreightOrVatParams,
  GetCartItemListRes,
  PriceRequest,
  PriceResponse,
  Product,
  UpdateCartItemRequest,
  PromotionSync,
} from '@/types/cart';
import { http } from './http.service';

class CartService {
  getAvailability;
  constructor() {
    this.getAvailability = this.useGetAvailability();
  }
  getCartList = async () => {
    const response = await http.get<GetCartItemListRes>(`/api/cart/item/list`);
    return response;
  };

  addToCart = async (product: Product) => {
    const response = await http.post(`/api/cart/item`, product);
    return response;
  };

  updateCart = async (params: UpdateCartItemRequest) => {
    const response = await http.post(`/api/cart/update/item`, params);
    return response;
  };

  deleteCart = async (ids: string[]) => {
    const response = await http.delete(`/api/cart/item`, {
      data: {
        catalogNumbers: ids,
      },
    });
    return response;
  };

  getFreightOrVat = async (data: FreightOrVatParams) => {
    const response = await http.post<{
      freight: string;
      vatRatio: string;
    }>('/api/cart/queryFreightAndVat', data);
    return response;
  };

  getProductsAvailability = async (item: { catalogNumber: string; requestQuantity: number }) => {
    return (await this.getAvailability(item)) as Availability;
  };
  getProductsBatchAvailability = async (itemsArray: { catalogNumber: string; requestQuantity: number }[]) => {
    const response = await http.post<Availability[]>('/api/availability/daysAndStock', itemsArray);
    return response;
  };
  useGetAvailability = () => {
    const resolves = [];
    let loading = false;
    return async (item: { requestQuantity: number; catalogNumber: string }): Promise<Availability> => {
      return new Promise<Availability>(async (resolve, reject) => {
        resolves.push([item, resolve, reject]);
        if (!loading) {
          while (resolves.length > 0) {
            const resolver = resolves.shift();
            loading = true;
            try {
              const response = await http.post<Availability>('/api/availability/daysAndStock', [resolver[0]]);
              if (response.code === '200') {
                resolver[1](response.data[0]);
              } else {
                resolver[2](response);
              }
            } catch (error) {
              resolver[2](error);
            } finally {
              loading = false;
            }
          }
        }
      });
    };
  };

  getProductPrice = async (
    itemList: PriceRequest[],
    shipToId?: string,
    billToId?: string,
    requestParams?: { pageType?: string }
  ) => {
    const response = await http.post<PriceResponse>('/api/price/batch', {
      itemList,
      shipToId,
      billToId,
      ...requestParams,
    });
    return response;
  };

  getProductPriceByPromoCode = async (
    itemList: PriceRequest[],
    shipToId: string,
    billToId: string,
    promotion: PromotionSync,
    shipToAddressKey: string
  ) => {
    const response = await http.post<PriceResponse>('/api/price/promo', {
      itemList,
      shipToId,
      billToId,
      shipToAddressKey,
      ...promotion,
    });
    return response;
  };

  evaluateManualPromotionPrice = async (
    itemList: PriceRequest[],
    shipToId: string,
    billToId: string,
    promotion: PromotionSync,
    shipToAddressKey: string
  ) => {
    const response = await http.post<PriceResponse>('/api/price/promo/evaluate', {
      itemList,
      billToId,
      shipToId,
      shipToAddressKey,
      manualPromotion: promotion,
    });
    return response;
  };

  addProductToCart = async (product: Pick<Product, 'catalogNumber' | 'quantity'>) => {
    const response = await http.post('/api/cart/item', product);
    return response;
  };

  updateProducts = async (
    params: Array<
      UpdateCartItemRequest & {
        catalogNumber: string;
      }
    >
  ) => {
    const response = await http.post<Product[]>('/api/cart/update/items', { itemList: params });
    return response;
  };

  getProductByCatalogId = async (catalogNumber: string) => {
    const response = await http.post<Product[]>('/api/cart/search/product', {
      catalogNumber,
    });
    return response;
  };

  batchGetProducts = async (catalogNumbers: string[]) => {
    const response = await http.post<Product[]>('/api/cart/search/products', {
      catalogNumber: catalogNumbers,
    });
    return response;
  };

  getQuotationData = async (addressKey) => {
    const response = await http.get('/api/order/quotation/pdf?addressKey=' + addressKey);
    return response;
  };
  getFWOData = async (request) => {
    const response = await http.post('/api/price/checkFWOPromotionInfo', request);
    return response;
  };
  getPromotionData = async (request) => {
    const response = await http.post('/api/price/getDisplayPromoForPage', request);
    return response;
  };
}

export const cartService = new CartService();
